import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Plus, Edit2, Trash2, Clock, AlertCircle, CheckCircle, X, Save, MessageSquarePlus, Server } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAppContext } from '../../context/AppContext';
import { toast } from 'sonner';
export interface MaintenanceUpdate {
  time: string;
  message: string;
}
export interface MaintenanceEvent {
  id: string;
  title: string;
  description: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'resolved';
  severity: 'maintenance' | 'minor' | 'major' | 'critical';
  affectedServers: string[];
  startTime: string;
  endTime?: string;
  updates: MaintenanceUpdate[];
}
export function MaintenanceManager() {
  const {
    serverConnections
  } = useAppContext();
  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<MaintenanceEvent | null>(null);
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<MaintenanceEvent['status']>('scheduled');
  const [severity, setSeverity] = useState<MaintenanceEvent['severity']>('maintenance');
  const [affectedServers, setAffectedServers] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  // Update form state
  const [updateMessage, setUpdateMessage] = useState('');
  useEffect(() => {
    loadEvents();
  }, []);
  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const {
        data,
        error
      } = await supabase.from('site_settings').select('value').eq('key', 'maintenance_events').maybeSingle();
      if (error && error.code !== 'PGRST116') throw error; // Ignore not found
      if (data?.value) {
        setEvents(data.value as MaintenanceEvent[]);
      }
    } catch (error) {
      console.error('Failed to load maintenance events:', error);
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };
  const saveEvents = async (newEvents: MaintenanceEvent[]) => {
    try {
      setIsSaving(true);
      const {
        error
      } = await supabase.from('site_settings').upsert({
        key: 'maintenance_events',
        value: newEvents
      }, {
        onConflict: 'key'
      });
      if (error) throw error;
      setEvents(newEvents);
      toast.success('Events saved successfully');
    } catch (error) {
      console.error('Failed to save events:', error);
      toast.error('Failed to save events');
    } finally {
      setIsSaving(false);
    }
  };
  const handleOpenModal = (event?: MaintenanceEvent) => {
    if (event) {
      setEditingEvent(event);
      setTitle(event.title);
      setDescription(event.description);
      setStatus(event.status);
      setSeverity(event.severity);
      setAffectedServers(event.affectedServers || []);
      // Format dates for datetime-local input
      const start = new Date(event.startTime);
      setStartTime(new Date(start.getTime() - start.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
      if (event.endTime) {
        const end = new Date(event.endTime);
        setEndTime(new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
      } else {
        setEndTime('');
      }
    } else {
      setEditingEvent(null);
      setTitle('');
      setDescription('');
      setStatus('scheduled');
      setSeverity('maintenance');
      setAffectedServers([]);
      const now = new Date();
      setStartTime(new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
      setEndTime('');
    }
    setIsModalOpen(true);
  };
  const handleSaveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    const newEvent: MaintenanceEvent = {
      id: editingEvent ? editingEvent.id : `evt_${Date.now()}`,
      title,
      description,
      status,
      severity,
      affectedServers,
      startTime: new Date(startTime).toISOString(),
      endTime: endTime ? new Date(endTime).toISOString() : undefined,
      updates: editingEvent ? editingEvent.updates : []
    };
    let newEvents;
    if (editingEvent) {
      newEvents = events.map(evt => evt.id === editingEvent.id ? newEvent : evt);
    } else {
      newEvents = [newEvent, ...events];
    }
    await saveEvents(newEvents);
    setIsModalOpen(false);
  };
  const handleDeleteEvent = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      const newEvents = events.filter(evt => evt.id !== id);
      await saveEvents(newEvents);
    }
  };
  const handleOpenUpdateModal = (event: MaintenanceEvent) => {
    setEditingEvent(event);
    setUpdateMessage('');
    setIsUpdateModalOpen(true);
  };
  const handleAddUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent || !updateMessage.trim()) return;
    const newUpdate: MaintenanceUpdate = {
      time: new Date().toISOString(),
      message: updateMessage.trim()
    };
    const updatedEvent = {
      ...editingEvent,
      updates: [newUpdate, ...(editingEvent.updates || [])]
    };
    const newEvents = events.map(evt => evt.id === editingEvent.id ? updatedEvent : evt);
    await saveEvents(newEvents);
    setIsUpdateModalOpen(false);
  };
  const toggleServer = (serverId: string) => {
    if (affectedServers.includes(serverId)) {
      setAffectedServers(affectedServers.filter(id => id !== serverId));
    } else {
      setAffectedServers([...affectedServers, serverId]);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
      case 'in-progress':
        return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      case 'completed':
      case 'resolved':
        return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      default:
        return 'text-slate-500 bg-slate-500/10 border-slate-500/20';
    }
  };
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'maintenance':
        return 'text-blue-500';
      case 'minor':
        return 'text-amber-500';
      case 'major':
        return 'text-orange-500';
      case 'critical':
        return 'text-red-500';
      default:
        return 'text-slate-500';
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-id="element-714">
        <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-715" />
      </div>;
  }
  return <div className="space-y-8" data-id="element-716">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-717">
        <div data-id="element-718">
          <h1 className="text-2xl font-bold text-white mb-2" data-id="element-719">
            Maintenance & Incidents
          </h1>
          <p className="text-slate-400" data-id="element-720">
            Manage server maintenance schedules and report incidents.
          </p>
        </div>
        <button onClick={() => handleOpenModal()} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-xl font-medium transition-colors" data-id="element-721">
          <Plus size={18} data-id="element-722" />
          Report Incident / Maintenance
        </button>
      </div>

      {events.length === 0 ? <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center" data-id="element-723">
          <Activity size={48} className="text-slate-600 mx-auto mb-4" data-id="element-724" />
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-725">No Events Found</h3>
          <p className="text-slate-400" data-id="element-726">
            There are no maintenance events or incidents recorded.
          </p>
        </div> : <div className="space-y-4" data-id="element-727">
          {events.map(event => <motion.div key={event.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-slate-900 border border-slate-800 rounded-2xl p-6" data-id="element-728">
              <div className="flex flex-col md:flex-row justify-between gap-4 mb-4" data-id="element-729">
                <div data-id="element-730">
                  <div className="flex items-center gap-3 mb-2" data-id="element-731">
                    <h3 className="text-lg font-bold text-white" data-id="element-732">
                      {event.title}
                    </h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getStatusColor(event.status)}`} data-id="element-733">
                      {event.status.replace('-', ' ')}
                    </span>
                    <span className={`text-xs font-medium capitalize flex items-center gap-1 ${getSeverityColor(event.severity)}`} data-id="element-734">
                      <AlertCircle size={14} data-id="element-735" />
                      {event.severity}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm" data-id="element-736">{event.description}</p>
                </div>
                <div className="flex items-center gap-2 md:self-start" data-id="element-737">
                  <button onClick={() => handleOpenUpdateModal(event)} className="p-2 text-slate-400 hover:text-fox-500 bg-slate-950 rounded-lg border border-slate-800 transition-colors" title="Add Update" data-id="element-738">
                    <MessageSquarePlus size={16} data-id="element-739" />
                  </button>
                  <button onClick={() => handleOpenModal(event)} className="p-2 text-slate-400 hover:text-white bg-slate-950 rounded-lg border border-slate-800 transition-colors" title="Edit Event" data-id="element-740">
                    <Edit2 size={16} data-id="element-741" />
                  </button>
                  <button onClick={() => handleDeleteEvent(event.id)} className="p-2 text-slate-400 hover:text-red-500 bg-slate-950 rounded-lg border border-slate-800 transition-colors" title="Delete Event" data-id="element-742">
                    <Trash2 size={16} data-id="element-743" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4 bg-slate-950 p-3 rounded-xl border border-slate-800" data-id="element-744">
                <div className="flex items-center gap-1.5" data-id="element-745">
                  <Clock size={14} data-id="element-746" />
                  <span data-id="element-747">
                    Started: {new Date(event.startTime).toLocaleString()}
                  </span>
                </div>
                {event.endTime && <div className="flex items-center gap-1.5" data-id="element-748">
                    <CheckCircle size={14} data-id="element-749" />
                    <span data-id="element-750">
                      Ended: {new Date(event.endTime).toLocaleString()}
                    </span>
                  </div>}
                <div className="flex items-center gap-1.5" data-id="element-751">
                  <Server size={14} data-id="element-752" />
                  <span data-id="element-753">
                    {event.affectedServers?.length || 0} Servers Affected
                  </span>
                </div>
              </div>

              {event.updates && event.updates.length > 0 && <div className="mt-4 space-y-3 border-t border-slate-800 pt-4" data-id="element-754">
                  <h4 className="text-sm font-medium text-slate-300 mb-2" data-id="element-755">
                    Updates
                  </h4>
                  {event.updates.map((update, i) => <div key={i} className="flex gap-3 text-sm" data-id="element-756">
                      <div className="text-slate-500 whitespace-nowrap shrink-0" data-id="element-757">
                        {new Date(update.time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                      </div>
                      <div className="text-slate-300" data-id="element-758">{update.message}</div>
                    </div>)}
                </div>}
            </motion.div>)}
        </div>}

      {/* Event Modal */}
      <AnimatePresence data-id="element-759">
        {isModalOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsModalOpen(false)} data-id="element-760" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-761">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl my-8" data-id="element-762">
                <div className="flex items-center justify-between p-6 border-b border-slate-800 sticky top-0 bg-slate-900 rounded-t-2xl z-10" data-id="element-763">
                  <h2 className="text-xl font-bold text-white" data-id="element-764">
                    {editingEvent ? 'Edit Event' : 'New Incident / Maintenance'}
                  </h2>
                  <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-765">
                    <X size={24} data-id="element-766" />
                  </button>
                </div>

                <form onSubmit={handleSaveEvent} className="p-6 space-y-6" data-id="element-767">
                  <div className="space-y-2" data-id="element-768">
                    <label className="text-sm font-medium text-slate-300" data-id="element-769">
                      Title
                    </label>
                    <input type="text" required value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500" placeholder="e.g. Scheduled Network Maintenance" data-id="element-770" />
                  </div>

                  <div className="space-y-2" data-id="element-771">
                    <label className="text-sm font-medium text-slate-300" data-id="element-772">
                      Description
                    </label>
                    <textarea required value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 resize-none" placeholder="Describe the event..." data-id="element-773" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-774">
                    <div className="space-y-2" data-id="element-775">
                      <label className="text-sm font-medium text-slate-300" data-id="element-776">
                        Status
                      </label>
                      <select value={status} onChange={e => setStatus(e.target.value as any)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500" data-id="element-777">
                        <option value="scheduled" data-id="element-778">Scheduled</option>
                        <option value="in-progress" data-id="element-779">In Progress</option>
                        <option value="completed" data-id="element-780">Completed</option>
                        <option value="resolved" data-id="element-781">Resolved</option>
                      </select>
                    </div>
                    <div className="space-y-2" data-id="element-782">
                      <label className="text-sm font-medium text-slate-300" data-id="element-783">
                        Severity
                      </label>
                      <select value={severity} onChange={e => setSeverity(e.target.value as any)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500" data-id="element-784">
                        <option value="maintenance" data-id="element-785">Maintenance (Blue)</option>
                        <option value="minor" data-id="element-786">Minor Incident (Yellow)</option>
                        <option value="major" data-id="element-787">Major Incident (Orange)</option>
                        <option value="critical" data-id="element-788">Critical Outage (Red)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-789">
                    <div className="space-y-2" data-id="element-790">
                      <label className="text-sm font-medium text-slate-300" data-id="element-791">
                        Start Time
                      </label>
                      <input type="datetime-local" required value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 [color-scheme:dark]" data-id="element-792" />
                    </div>
                    <div className="space-y-2" data-id="element-793">
                      <label className="text-sm font-medium text-slate-300" data-id="element-794">
                        End Time (Optional)
                      </label>
                      <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 [color-scheme:dark]" data-id="element-795" />
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-796">
                    <label className="text-sm font-medium text-slate-300" data-id="element-797">
                      Affected Servers
                    </label>
                    <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 max-h-48 overflow-y-auto space-y-2" data-id="element-798">
                      {serverConnections.length === 0 ? <p className="text-sm text-slate-500 text-center py-2" data-id="element-799">
                          No servers configured.
                        </p> : serverConnections.map(server => <label key={server.id} className="flex items-center gap-3 p-2 hover:bg-slate-900 rounded-lg cursor-pointer transition-colors" data-id="element-800">
                            <input type="checkbox" checked={affectedServers.includes(server.id)} onChange={() => toggleServer(server.id)} className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-fox-600 focus:ring-fox-500 focus:ring-offset-slate-950" data-id="element-801" />
                            <div data-id="element-802">
                              <div className="text-sm font-medium text-white" data-id="element-803">
                                {server.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-804">
                                {server.hostname}
                              </div>
                            </div>
                          </label>)}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-805">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-white transition-colors" data-id="element-806">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-6 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50" data-id="element-807">
                      {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-808" /> : <Save size={18} data-id="element-809" />}
                      Save Event
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Add Update Modal */}
      <AnimatePresence data-id="element-810">
        {isUpdateModalOpen && editingEvent && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setIsUpdateModalOpen(false)} data-id="element-811" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-812">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl" data-id="element-813">
                <div className="flex items-center justify-between p-6 border-b border-slate-800" data-id="element-814">
                  <h2 className="text-lg font-bold text-white" data-id="element-815">Add Update</h2>
                  <button onClick={() => setIsUpdateModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-816">
                    <X size={20} data-id="element-817" />
                  </button>
                </div>
                <form onSubmit={handleAddUpdate} className="p-6 space-y-4" data-id="element-818">
                  <div className="space-y-2" data-id="element-819">
                    <label className="text-sm font-medium text-slate-300" data-id="element-820">
                      Update Message
                    </label>
                    <textarea required value={updateMessage} onChange={e => setUpdateMessage(e.target.value)} rows={4} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 resize-none" placeholder="e.g. We have identified the issue and are working on a fix..." data-id="element-821" />
                  </div>
                  <div className="flex justify-end gap-3 pt-2" data-id="element-822">
                    <button type="button" onClick={() => setIsUpdateModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition-colors" data-id="element-823">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSaving || !updateMessage.trim()} className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50" data-id="element-824">
                      Post Update
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}